import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { API } from '../config';
import { pct, num, statoAgente, durata, fette, catena, coloreAllarme } from './indagaData';
import './indaga.css';

/* La dashboard dell'agente Indaga: l'unico che non ce l'aveva.

   Risponde a tre domande, in quest'ordine — sta lavorando? rende? devo intervenire? — e la
   seconda non si misura in indagini fatte ma nella CATENA: quanti degli indirizzi completati
   sono poi diventati un lead esportato. Gli allarmi stanno in cima apposta: una dashboard
   che bisogna ricordarsi di guardare per accorgersi di un problema non la guarda nessuno. */

const PERIODI = [{ g: 7, l: '7 giorni' }, { g: 30, l: '30 giorni' }, { g: 90, l: '90 giorni' }];
const gg = (s) => (s || '').slice(8, 10) + '/' + (s || '').slice(5, 7);

export default function IndagaDashboardPage() {
  const [giorni, setGiorni] = useState(30);
  const [d, setD] = useState(null);
  const [stato, setStato] = useState('carico');     // 'carico' | 'ok' | 'errore'
  const [cambio, setCambio] = useState(false);
  // le proposte: indirizzi trovati ma non provati, che una persona puo' accettare
  const [proposte, setProposte] = useState(null);
  const [inCorso, setInCorso] = useState('');

  const carica = useCallback((silenzioso) => {
    if (!silenzioso) setStato('carico');
    return axios.get(`${API}/indaga/statistiche?giorni=${giorni}`)
      .then((r) => { setD(r.data); setStato('ok'); })
      .catch(() => { if (!silenzioso) setStato('errore'); });
  }, [giorni]);

  const caricaProposte = useCallback(() => {
    axios.get(`${API}/indaga/proposte?limit=100`)
      .then((r) => setProposte(r.data))
      .catch(() => setProposte({ items: [], totale: 0 }));
  }, []);

  useEffect(() => { carica(); }, [carica]);
  useEffect(() => { caricaProposte(); }, [caricaProposte]);

  async function decidi(id, accetta) {
    setInCorso(id);
    try {
      await axios.post(`${API}/indaga/proposte/${id}/${accetta ? 'applica' : 'scarta'}`);
      toast.success(accetta ? 'Indirizzo scritto in archivio' : 'Proposta scartata');
      setProposte((p) => ({ totale: Math.max(0, (p?.totale || 1) - 1),
                            items: (p?.items || []).filter((x) => x.id !== id) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Non sono riuscito a registrare la scelta');
    } finally {
      setInCorso('');
    }
  }
  // battito: lo stato dell'agente cambia da solo mentre guardi la pagina
  useEffect(() => {
    const t = setInterval(() => carica(true), 20000);
    return () => clearInterval(t);
  }, [carica]);

  async function interruttore(attivo) {
    setCambio(true);
    try {
      await axios.post(`${API}/indaga/agente`, { attivo });
      toast.success(attivo ? 'Agente Indaga avviato' : 'Agente Indaga fermato');
      await carica(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Non sono riuscito a cambiare lo stato');
    } finally {
      setCambio(false);
    }
  }

  if (stato === 'carico' && !d) return <div className="ind-msg">Carico le statistiche…</div>;
  if (stato === 'errore') {
    return (
      <div className="ind-msg">
        Non riesco a leggere le statistiche dell'agente.
        <button className="btn btn-secondary" style={{ marginLeft: 10 }} onClick={() => carica()}>Riprova</button>
      </div>
    );
  }

  const s = statoAgente(d.stato);
  const anelli = catena(d.catena);
  const esiti = fette(d.imbuto);
  const resaEsportati = pct(d.catena?.esportati, d.catena?.completati);
  const andamento = (d.andamento || []).map((g) => ({ ...g, etichetta: gg(g.giorno) }));

  return (
    <div className="ind">
      <div className="ind-head">
        <h1>Agente Indaga</h1>
        <div className="ind-periodi">
          {PERIODI.map((p) => (
            <button key={p.g} className={`ind-pill${giorni === p.g ? ' on' : ''}`}
              onClick={() => setGiorni(p.g)}>{p.l}</button>
          ))}
        </div>
      </div>

      {/* Gli allarmi stanno in cima: sono il motivo per cui questa pagina esiste */}
      {(d.allarmi || []).map((a, i) => (
        <div key={i} className="ind-allarme" style={{ borderColor: coloreAllarme(a.livello),
          background: a.livello === 'grave' ? 'rgba(239,68,68,.08)' : 'rgba(245,158,11,.08)' }}>
          <span className="ind-allarme-p" style={{ background: coloreAllarme(a.livello) }} />
          {a.testo}
        </div>
      ))}

      {/* Stato dell'agente */}
      <div className="ind-stato">
        <div className="ind-stato-l">
          <span className={`ind-spia ${s.spia}`} style={{ background: s.colore }} />
          <div>
            <div className="ind-stato-t" style={{ color: s.colore }}>{s.testo}</div>
            <div className="ind-stato-s">
              {num(d.stato.fatti_oggi)} di {num(d.stato.tetto)} indagini oggi
              {d.stato.job && d.stato.job.status === 'running' && d.stato.job.current
                ? ` · sto lavorando «${d.stato.job.current}»` : ''}
            </div>
          </div>
        </div>
        <div className="ind-stato-r">
          <div className="ind-coda">
            <div className="ind-coda-v">{num(d.coda.eleggibili)}</div>
            <div className="ind-coda-l">
              in coda{d.coda.giorni_stimati != null ? ` · ${d.coda.giorni_stimati} giorni di lavoro` : ''}
            </div>
          </div>
          <button className={`btn ${d.stato.agente_attivo ? 'btn-secondary' : 'btn-primary'}`}
            disabled={cambio || !d.stato.abilitata}
            onClick={() => interruttore(!d.stato.agente_attivo)}>
            {d.stato.agente_attivo ? 'Ferma' : 'Avvia'}
          </button>
        </div>
      </div>

      {/* La redditività: la catena del valore */}
      <div className="ind-card">
        <h2>Quanto rende</h2>
        <p className="ind-sub">
          Di ogni indirizzo che l'agente completa, questo è quello che succede dopo. È la
          misura vera della resa: non quante indagini fa, ma quanti lead ne escono.
        </p>
        <div className="ind-catena">
          {anelli.map((a, i) => (
            <React.Fragment key={a.k}>
              {i > 0 && <div className="ind-freccia">→</div>}
              <div className="ind-anello">
                <div className="ind-anello-v">{num(a.n)}</div>
                <div className="ind-anello-l">{a.l}</div>
                {i > 0 && <div className="ind-anello-p">{a.pct}% dei completati</div>}
              </div>
            </React.Fragment>
          ))}
        </div>
        <div className="ind-frase">
          <strong>{resaEsportati}%</strong> degli indirizzi completati dall'agente è diventato
          un lead esportato.
        </div>
        {(d.prove || []).length > 0 && (
          <div className="ind-prove">
            Come si è arrivati alla prova:{' '}
            {d.prove.map((p) => `${p.fonte === 'piva' ? 'partita IVA' :
              p.fonte === 'via_coerente' ? 'via già nota' : 'toponomastica'} ${num(p.n)}`).join(' · ')}
          </div>
        )}
      </div>

      {/* L'imbuto degli esiti */}
      <div className="ind-card">
        <h2>Com'è andata · {num(d.imbuto.lavorati)} indagini</h2>
        <div className="ind-barra">
          {esiti.map((e) => (
            <div key={e.k} className="ind-fetta" style={{ width: `${e.pct}%`, background: e.c }}
              title={`${e.l}: ${num(e.n)} (${e.pct}%)`} />
          ))}
        </div>
        <div className="ind-legenda">
          {esiti.map((e) => (
            <div key={e.k} className="ind-voce">
              <span className="ind-punto" style={{ background: e.c }} />
              <span className="ind-voce-l">{e.l}</span>
              <span className="ind-voce-n">{num(e.n)}</span>
              <span className="ind-voce-p">{e.pct}%</span>
              <span className="ind-voce-d">{e.d}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Perché non si conferma */}
      {(d.motivi || []).length > 0 && (
        <div className="ind-card">
          <h2>Perché non si riesce a confermare</h2>
          <p className="ind-sub">
            Dal registro delle indagini (dal {d.registro_dal}). Sono problemi diversi e si
            risolvono in modi diversi: qui si vede su quale conviene intervenire.
          </p>
          <table className="ind-tab">
            <tbody>
              {d.motivi.map((m) => (
                <tr key={m.motivo}>
                  <td className="l">{m.etichetta}</td>
                  <td className="n">{num(m.n)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Le proposte da decidere a mano */}
      {proposte && proposte.items.length > 0 && (
        <div className="ind-card">
          <h2>Da decidere · {num(proposte.totale)}</h2>
          <p className="ind-sub">
            Qui l'agente l'azienda l'ha trovata ma non ha potuto provare che fosse lei, quindi
            non ha scritto niente. Guardando il nome della scheda accanto a quello del record
            si capisce in tre secondi quello che una regola non può decidere da sola.
          </p>
          <div className="ind-proposte">
            {proposte.items.map((r) => (
              <div key={r.id} className="ind-proposta">
                <div className="ind-p-chi">
                  <div className="ind-p-nome">{r.ragione_sociale || '—'}</div>
                  <div className="ind-p-sotto">
                    {r.numero || '—'}{r.comune ? ` · ${r.comune}` : ''}
                    {r.partita_iva ? ` · P.IVA ${r.partita_iva}` : ''}
                  </div>
                </div>
                <div className="ind-p-conf">
                  <div><span className="ind-p-et">nel record</span> {r.indirizzo_attuale || '—'}</div>
                  <div><span className="ind-p-et">trovato</span> <b>{r.indirizzo_proposto}</b></div>
                  <div className="ind-p-maps">
                    su Maps: {r.nome_su_maps || '—'}
                    {r.maps_url && <> · <a href={r.maps_url} target="_blank" rel="noreferrer">apri la scheda</a></>}
                  </div>
                  <div className="ind-p-motivo">{r.etichetta_motivo}</div>
                </div>
                <div className="ind-p-azioni">
                  <button className="btn btn-primary" disabled={inCorso === r.id}
                    onClick={() => decidi(r.id, true)}>È lei</button>
                  <button className="btn btn-secondary" disabled={inCorso === r.id}
                    onClick={() => decidi(r.id, false)}>Scarta</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* L'andamento */}
      <div className="ind-card">
        <h2>Andamento</h2>
        {andamento.length === 0 ? (
          <p className="ind-sub">
            Il registro delle indagini è partito il {d.registro_dal}: l'andamento compare man
            mano che l'agente lavora.
          </p>
        ) : (
          <>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <ComposedChart data={andamento} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--line, #e2e8f0)" />
                  <XAxis dataKey="etichetta" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="n" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="r" orientation="right" unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="n" dataKey="lavorati" name="Indagini" fill="#cbd5e1" barSize={26} />
                  <Bar yAxisId="n" dataKey="completati" name="Completate" fill="#22c55e" barSize={26} />
                  <Line yAxisId="r" dataKey="resa" name="Resa %" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="ind-tempo">
              <div><b>{durata(d.tempo.durata_media_s)}</b> per indagine</div>
              <div><b>{d.tempo.minuti_per_completato != null
                ? `${d.tempo.minuti_per_completato} min` : '—'}</b> di macchina per ogni indirizzo completato</div>
              <div><b>{num(d.tempo.indagini_registrate)}</b> indagini nel registro</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
